// bug with select and WebGLMap: https://github.com/openlayers/openlayers/issues/9688
// not going to be fixed apparently
// import { Select as SelectInteraction } from 'ol/interaction';

import {
  Divider,
  IconButton,
  ListItemIcon,
  makeStyles,
  Menu,
  MenuItem,
  Popover,
  Typography,
  useMediaQuery,
  useTheme,
} from '@material-ui/core';
import {
  Label as LabelIcon,
  LabelOff as LabelOffIcon,
  Map as MapIcon,
  Satellite as SatelliteIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  ZoomOutMap as ZoomOutMapIcon,
  Layers as LayersIcon,
} from '@material-ui/icons';
import { FormatListBulletedType as LegendIcon } from 'mdi-material-ui';
// import { MapLegend as LegendIcon } from 'mdi-material-ui';
import _ from 'lodash';
import { Map, View } from 'ol';
import { defaults as defaultControls } from 'ol/control';
import { applyTransform, createEmpty, extend, isEmpty } from 'ol/extent';
import { GeoJSON } from 'ol/format';
import { Point, Polygon } from 'ol/geom';
import { defaults as defaultInteractions } from 'ol/interaction';
import {
  Vector as VectorLayer,
  Tile as TileLayer,
  Image as ImageLayer,
} from 'ol/layer';
import 'ol/ol.css';
import '../../css/ol-centred-attribution.css';
import { getTransform } from 'ol/proj';
import { Cluster as ClusterSource, Vector as VectorSource } from 'ol/source';
import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  useMemo,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import {
  UPDATE_LIVE_LAYER_VISIBILITIES,
  UPDATE_LIVE_SETTINGS,
} from '../../actions';
import { getLiveStyle, preloadLiveIcons, setBaseLayers } from '../../mapStyles';
import { usePrevious } from '../../hooks';
import {
  typeIcons,
  singularToPluralTypeMap,
  pluralToSingularTypeMap,
  labelPaths,
} from './constants';
import LiveLegend from './LiveLegend';
import { statusZIndexByType } from '../../data/constants';

const useStyles = makeStyles((theme) => ({
  map: {
    width: '100%',
    height: '100%',
  },
  zoomInButton: {
    position: 'absolute',
    top: 5,
    left: 5,
    zIndex: 100,
  },
  zoomOutButton: {
    position: 'absolute',
    top: 40,
    left: 5,
    zIndex: 100,
  },
  fitButton: {
    position: 'absolute',
    top: 75,
    left: 5,
    zIndex: 100,
  },
  labelButton: {
    position: 'absolute',
    top: 110,
    left: 5,
    zIndex: 100,
  },
  layerToggleArea: {
    position: 'absolute',
    bottom: 5,
    left: 5,
    zIndex: 100,
  },
  layerToggleList: {
    zIndex: 100,
    display: 'flex',
    flexDirection: 'column',
    maxHeight: 'calc(100vh - 250px)',
    overflow: 'auto',
  },
  layerToggleButton: {
    position: 'relative',
    // height: 24
  },
  layerToggleIcon: {
    marginTop: -12,
  },
  legendToggleArea: {
    position: 'absolute',
    bottom: 5,
    right: 5,
    zIndex: 100,
  },
  legendToggleButton: {
    position: 'relative',
    // bottom: 5,
    // right: 5,
    // zIndex: 100,
    // height: 24
  },
  legendPopover: {
    width: 'calc(100% - 452px)',
    maxWidth: 750,
    [theme.breakpoints.down('xs')]: {
      width: '90%',
    },
    maxHeight: '90%',
  },
  diagonalContainer: {
    overflow: 'hidden',
    position: 'relative',
  },
  diagonal: {
    // when they are in an element not a menu
    // top: 2,
    // left: 17,
    // when they are in a menu
    top: -15,
    left: 7,
    transform: 'rotate(-45deg)',
    fontSize: 34,
    position: 'absolute',
  },
  diagonalWhite: {
    position: 'absolute',
    top: -15,
    left: 9,
    color: 'rgba(255,255,255,1.0)',
    transform: 'rotate(-45deg)',
    fontSize: 34,
  },
  layerButton: {
    position: 'absolute',
    bottom: 5,
    left: 5,
    zIndex: 100,
  },
  layerMenuItemText: {
    textTransform: 'capitalize',
    marginLeft: theme.spacing(1),
  },
  container: {
    position: 'relative',
    width: '100%',
    minWidth: 240,
    height: '100%',
    color: 'rgba(255,255,255,1.0)',
  },
  iconStyle: {
    minWidth: 24,
  },
}));

const layerZIndexOrder = [
  'objectives',
  'plans',
  'tasks',
  'locations',
  'vehicles',
  'people',
  'events',
  'incidents',
];

const isFilteredIn = (id, group, filteredInIdsByType) => {
  return (
    !(group in filteredInIdsByType) || filteredInIdsByType[group][id] === true
  );
};

function safeRemoveFeature(source, id) {
  const feature = source.getFeatureById(id);
  if (feature) {
    source.removeFeature(feature);
  }
}

function isShown(layerVisibilities, type) {
  // show layer if it's not specified either way or if it set to true
  return !(type in layerVisibilities) || layerVisibilities[type];
}

const STALE = 'stale';
const FILTERED = 'filtered';

const ICON_ZINDEX = 20;
const POLY_ZINDEX = 5;
const STALE_ZINDEX = 10;
const FILTERED_ZINDEX = 0;

function statusToZIndex(status, type) {
  const ffs = statusZIndexByType;
  const zIndex = ffs[pluralToSingularTypeMap[type]]?.[status];
  if (zIndex) {
    return zIndex;
  }

  // default if not above
  switch (status) {
    case 'emergency':
    case 'malfunctioning':
      return 10;
    case 'assigned':
    case 'warning equipment':
      return 7;
    case 'default':
      return 2;
    case 'inactive':
    case 'parked':
    case 'closed':
      return 1;
    case 'stale':
      return 0;
    default:
      return 2;
  }
}

const staleable = ['vehicles', 'people'];
const polygonable = ['locations', 'plans', 'objectives'];

// lower layers are stale vehicles and people or polygons
function shouldGoInLower(feature) {
  const { type, status } = feature.getProperties();
  const pluralType =
    type in singularToPluralTypeMap ? singularToPluralTypeMap[type] : type;

  return (
    polygonable.includes(pluralType) ||
    (staleable.includes(pluralType) && status === STALE)
  );
}

// upper layers are every icon except stale vehicles and people
function shouldGoInUpper(feature) {
  const { type, status } = feature.getProperties();
  const pluralType =
    type in singularToPluralTypeMap ? singularToPluralTypeMap[type] : type;

  return !(staleable.includes(pluralType) && status === STALE);
}

const {
  labelOverridePaths,
  mapClusterDistance,
  disableRotatedIconsOnMobile,
} = window.config.liveOptions;

// JL use all-type filter instead
// toFeature(element, group, filterType, filterIds) {
const toFeatureObj = (
  element,
  group,
  filteredInIdsByType,
  followedIdsByType
) => {
  if (!element) {
    return null;
  }

  const filteredIn = isFilteredIn(element.id, group, filteredInIdsByType);
  const followed = followedIdsByType[group]?.[element.id];
  const interiorPoint = element.boundary
    ? new Polygon(element.boundary.coordinates).getInteriorPoint()
    : null;
  const boundaryWithPoint = !interiorPoint
    ? undefined
    : {
        type: 'GeometryCollection',
        geometries: [
          {
            type: 'Point',
            coordinates: interiorPoint.getCoordinates(),
          },
          element.boundary,
        ],
      };
  // const { coordinates } = (interiorPoint || element.position || { coordinates: null});
  const clusterPoint =
    interiorPoint ||
    (element.position && new Point(element.position.coordinates)) ||
    (element.point && new Point(element.point.coordinates));
  clusterPoint &&
    clusterPoint.applyTransform(getTransform('EPSG:4326', 'EPSG:3857'));
  const status = element.status; //(element, group);
  const zIndex = followed ? 10 : statusToZIndex(status, group);
  const labelOverride =
    labelOverridePaths?.[group] && _.get(element, labelOverridePaths[group]);

  const commonProperties = {
    clusterPoint,
    status,
    zIndex,
    type: pluralToSingularTypeMap[group],
    route: `/live/${group}/${encodeURIComponent(element.id)}`,
    filteredIn,
    followed,
    label:
      (typeof labelOverride === 'string' && labelOverride) ||
      _.get(element, labelPaths[group]),
  };

  switch (group) {
    case 'vehicles':
      return element.position
        ? {
            geometry: element.position,
            id: element.id,
            type: 'Feature',
            properties: {
              ...commonProperties,
              headingDegrees: element.headingDegrees,
              ignitionOn: element.ignitionOn,
              registrationNumber: element.registrationNumber,
              active: element.ignitionOn,
            },
          }
        : null;
    case 'people':
      return element.position
        ? {
            geometry: element.position,
            id: element.code,
            type: 'Feature',
            properties: {
              ...commonProperties,
              collarNumber: element.collarNumber,
            },
          }
        : null;
    case 'events':
      return {
        geometry: element.point,
        id: element.id,
        type: 'Feature',
        properties: {
          ...commonProperties,
          code: element.vehicle.registrationNumber, // doesn't seem to have //element.code,
        },
      };
    case 'incidents':
      return {
        geometry: element.point,
        id: element.id,
        type: 'Feature',
        properties: {
          ...commonProperties,
          number: element.number, // doesn't seem to have //element.code,
          subtype: element.grade,
        },
      };
    case 'plans':
      return {
        geometry: boundaryWithPoint,
        id: element.id,
        type: 'Feature',
        properties: commonProperties,
      };
    case 'objectives':
      return {
        geometry: boundaryWithPoint,
        id: element.id,
        type: 'Feature',
        properties: {
          ...commonProperties,
          subtype: element.type,
        },
      };
    case 'locations':
      // make locations have a geometry collection
      return {
        geometry: boundaryWithPoint,
        id: element.id,
        type: 'Feature',
        properties: {
          ...commonProperties,
          name: element.name,
        },
      };
    // ...
    default:
      return null;
  }
};

// there are a lot of params but this is to avoid writing the function in twice
// due to useEffect deps
function upsertMapFeature({
  features,
  type,
  id,
  itemSources,
  itemSourcesLower,
  itemSourcesFilteredOut,
  getFeatureByTypeId,
  filteredInIdsByType,
  followedIdsByType,
}) {
  let existing = getFeatureByTypeId(type, id);
  let currentlyIn = [];
  if (existing) {
    currentlyIn = [
      itemSources,
      itemSourcesLower,
      itemSourcesFilteredOut,
    ].filter((source) => source.current[type].hasFeature(existing));
  }

  // if it's not there now, the update was a delete, remove from map
  const resource = features[type][id];
  if (!resource) {
    currentlyIn.forEach((source) =>
      source.current[type].removeFeature(existing)
    );
    return;
  }

  var featureObj = toFeatureObj(
    resource,
    type,
    filteredInIdsByType,
    followedIdsByType
  );
  if (!featureObj) {
    return;
  }

  var updated = new GeoJSON().readFeature(featureObj, {
    featureProjection: 'EPSG:3857',
  });

  // the feature can go in upper (icons), lower (stale/polygon) or filtered out sources
  let shouldGoIn = [];
  if (isFilteredIn(id, type, filteredInIdsByType)) {
    if (shouldGoInUpper(updated)) {
      shouldGoIn.push(itemSources);
    }

    if (shouldGoInLower(updated)) {
      shouldGoIn.push(itemSourcesLower);
    }
  } else {
    shouldGoIn.push(itemSourcesFilteredOut);
  }

  if (existing) {
    existing.setGeometry(updated.getGeometry());
    existing.setProperties(updated.getProperties());
  } else {
    shouldGoIn.forEach((source) => source.current[type].addFeature(updated));
  }
}

function LiveMap({
  authorisedTypes,
  hoveredItem,
  onHover,
  selectedItem,
  onSelect,
}) {
  const { type = 'vehicles', id: encodedId, subId: encodedSubId } = useParams();
  const id = encodedId && decodeURIComponent(encodedId);
  const subId = encodedSubId && decodeURIComponent(encodedSubId);
  const idRef = useRef(id);
  idRef.current = id;

  const [zoomDisabled, setZoomDisabled] = useState({ in: false, out: false });
  const [satelliteMapVisible, setSatelliteMapVisible] = useState(false);
  const [layerToggles, setLayerToggles] = useState({});
  const [refreshToggle, setRefreshToggle] = useState(false);
  const [layerAnchorEl, setLayerAnchorEl] = useState(null);
  const [legendAnchorEl, setLegendAnchorEl] = useState(null);
  const [allowSatellite, setAllowSatellite] = useState(false);

  const classes = useStyles();

  const mapDiv = useRef(null);
  const mapRef = useRef(null);
  const view = mapRef.current && mapRef.current.getView();
  const itemSources = useRef({});
  const itemSourcesLower = useRef({});
  const itemSourcesFilteredOut = useRef({});
  const itemLayers = useRef([]);
  const itemLayersLower = useRef([]);
  const itemLayersFilteredOut = useRef([]);
  const hoverSource = useRef({});
  const selectSource = useRef({});
  const showLabels = useRef(false);

  const vehicles = useSelector((state) => state.live.vehicles);
  const people = useSelector((state) => state.live.people);
  const locations = useSelector((state) => state.live.locations);
  const events = useSelector((state) => state.live.events);
  const incidents = useSelector((state) => state.live.incidents);
  const callSigns = useSelector((state) => state.live.callSigns);
  const tasks = useSelector((state) => state.live.tasks);
  const objectives = useSelector((state) => state.live.objectives);
  const plans = useSelector((state) => state.live.plans);
  const planFeatures = useSelector((state) => state.live.planFeatures);
  const tags = useSelector((state) => state.live.tags);
  const layerVisibilities = useSelector(
    (state) => state.live.layerVisibilities
  );
  const followedIdsByType = useSelector(
    (state) =>
      state.live.followedIdsByTypeOverride || state.live.followedIdsByType
  );
  const prevFollowedIdsByType = usePrevious(followedIdsByType);
  const filteredInIdsByTypeOverride = useSelector(
    (state) => state.live.filteredInIdsByTypeOverride
  );
  const filteredInIdsByType = useSelector(
    (state) =>
      state.live.filteredInIdsByTypeOverride || state.live.filteredInIdsByType
  );
  const prevFilteredInIdsByType = usePrevious(filteredInIdsByType);

  const showStale = isShown(layerVisibilities, STALE);
  const showStaleRef = useRef();
  showStaleRef.current = showStale;

  const dispatch = useDispatch();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'), { noSsr: true });
  const allowRotations = !(disableRotatedIconsOnMobile && isMobile);
  const fitOptions = { maxZoom: 16, duration: 750 };
  const fitOptionsMobile = {
    ...fitOptions,
    padding: [0, 0, isMobile ? 400 : 0, 0],
  };

  const settings = useSelector((state) => state.live.settings);
  const prevSettings = usePrevious(settings);
  useEffect(() => {
    if (settings && settings !== prevSettings) {
      if (settings.showLabels !== prevSettings?.showLabels) {
        showLabels.current = settings.showLabels;
        mapRef.current &&
          mapRef.current.getLayers().forEach((layer) => layer.changed());
        setRefreshToggle(!refreshToggle);
      }
    }
  }, [settings, prevSettings, setRefreshToggle, refreshToggle]);

  function changeSettings(settings) {
    dispatch({
      type: UPDATE_LIVE_SETTINGS,
      payload: settings,
    });
  }

  const features = useMemo(
    () => ({
      vehicles,
      people,
      locations,
      events,
      incidents,
      callSigns,
      tasks,
      objectives,
      plans,
    }),
    [
      vehicles,
      people,
      locations,
      events,
      incidents,
      callSigns,
      tasks,
      objectives,
      plans,
    ]
  );

  const prevFeatures = usePrevious(features);
  const featuresUpdated = prevFeatures !== features;
  const updatedIds = useMemo(() => {
    let updatedIds = {};

    Object.keys(features).forEach((key) => {
      const prev = (prevFeatures || {})[key] || {};
      const curr = features[key];
      //let thisChanged = false;

      // how many have changed?
      // updated ones weren't in the previous features or changed
      const updated = Object.keys(curr).filter((k) => prev[k] !== curr[k]);
      // deleted ones are previous ones that aren't in the current features
      const deleted = Object.keys(prev).filter((k) => !curr[k]);

      if (updated.length || deleted.length) {
        updatedIds[key] = updated.concat(deleted);
      }

      // if (thisChanged) console.log("updated Ids changed...", key);
    });

    return updatedIds;
  }, [features, prevFeatures]);

  // checks filtered in source and filtered out source
  const getFeatureByTypeId = useCallback((type, id) => {
    if (id) {
      if (type in singularToPluralTypeMap) {
        type = singularToPluralTypeMap[type];
      }

      function tryGetFrom(sources) {
        if (type in sources.current) {
          return sources.current[type].getFeatureById(id);
        }

        return false;
      }

      let target = tryGetFrom(itemSources);
      if (target) {
        return target;
      }
      target = tryGetFrom(itemSourcesLower);
      if (target) {
        return target;
      }

      target = tryGetFrom(itemSourcesFilteredOut);
      return target;
    }

    return null;
  }, []);

  // used when setting a source to have a single feature (select and hover layers)
  const setSourceFeature = useCallback(
    (type, id, source) => {
      const feature = getFeatureByTypeId(type, id);
      if (feature) {
        source.clear();
        source.addFeature(feature);
      }

      return feature;
    },
    [getFeatureByTypeId]
  );

  // get whatever feature was clicked
  const featureFromEvent = (event) => {
    const pixel = mapRef.current.getEventPixel(event.originalEvent);
    const features = mapRef.current.getFeaturesAtPixel(pixel);

    if (!features?.length) {
      return null;
    }

    // otherwise return the top one
    // if it's a cluster, return the one with the highest z index
    const topFeature = features[0];
    if (topFeature.get('features')) {
      return _.maxBy(
        topFeature.get('features'),
        (f) => f.getProperties().zIndex || 0
      );
    }

    return topFeature;
  };

  const handleHover = (event) => {
    if (!event.dragging) {
      //} && !selectedItem.id) {
      const feature = featureFromEvent(event);

      if (feature) {
        const individualType = feature.getProperties().type;
        const type = singularToPluralTypeMap[individualType];
        const newHoveredItem = {
          id: feature.getId(),
          type,
        };

        if (!_.isEqual(newHoveredItem, hoveredItem)) {
          hoverSource.current.clear();
          hoverSource.current.addFeature(feature);
          onHover(newHoveredItem);
        }
      } else {
        hoverSource.current.clear();
        if (hoveredItem.id) {
          // if there was one, clear it now
          onHover({});
        }
      }
    }
  };

  const debouncedHover = _.debounce(handleHover, 150);

  function getClusterPoint(feature) {
    return feature.getProperties().clusterPoint;
  }

  // use a current ref so don't have to reregister map clicks every
  // time parent handler changes
  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;

  // set up map on startup
  useEffect(
    () => {
      if (mapRef.current !== null) {
        return;
      }
      // console.log("set up map once!");

      const getDefaultStyle = (feature) => {
        return getLiveStyle({
          layer: 'default',
          feature,
          showLabels: showLabels.current,
          zoom: mapRef.current.getView().getZoom(),
          allowRotations,
          showStale: showStaleRef.current,
        });
      };

      const getIconsOnlyStyle = (feature) => {
        return getLiveStyle({
          layer: 'default',
          feature,
          showLabels: showLabels.current,
          zoom: mapRef.current.getView().getZoom(),
          iconsOnly: true,
          allowRotations,
          showStale: showStaleRef.current,
        });
      };

      const getPolygonsOnlyStyle = (feature) => {
        return getLiveStyle({
          layer: 'default',
          feature,
          showLabels: showLabels.current,
          zoom: mapRef.current.getView().getZoom(),
          polygonsOnly: true,
          polygonIsSelected: idRef.current === feature.getId(),
          allowRotations,
          showStale: showStaleRef.current,
        });
      };

      const getSelectStyle = (feature) => {
        return getLiveStyle({
          layer: 'select',
          feature,
          showLabels: showLabels.current,
          zoom: mapRef.current.getView().getZoom(),
          allowRotations,
          showStale: showStaleRef.current,
        });
      };

      const getHoverStyle = (feature) => {
        return getLiveStyle({
          layer: 'hover',
          feature,
          showLabels: showLabels.current,
          zoom: mapRef.current.getView().getZoom(),
          allowRotations,
          showStale: showStaleRef.current,
        });
      };

      function isShown(type) {
        // show layer if it's not in the state or use the state value
        return !(type in layerVisibilities) || layerVisibilities[type];
      }

      // build this up then set once
      const layerToggles = {};
      const clusterDistance = mapClusterDistance || 0;

      const initSourceAndLayer = (
        sourceArray,
        layerArray,
        type,
        opacity,
        zIndex,
        style,
        show,
        shouldCluster
      ) => {
        sourceArray[type] = new VectorSource({ features: [] });

        const layer = new VectorLayer({
          title: type,
          source:
            clusterDistance > 0 && shouldCluster
              ? new ClusterSource({
                  distance: clusterDistance,
                  source: sourceArray[type],
                  geometryFunction: getClusterPoint,
                })
              : sourceArray[type],
          style: style || getDefaultStyle,
          renderMode: 'image',
          opacity: opacity,
          visible: show,
        });

        // I want to control when changed happens, otherwise changing a single
        // features geometry or properties or removing it causes a rerender
        // so create a new "changeComplete" method I can call when needed
        // and override changed so it does nothing
        function ignore() {}
        sourceArray[type].changeComplete = sourceArray[type].changed;
        sourceArray[type].changed = ignore;

        layer.setZIndex(zIndex + layerZIndexOrder.indexOf(type));

        layerArray.push(layer);

        if (!(type in layerToggles)) {
          layerToggles[type] = { show, layers: [] };
        }
        layerToggles[type].layers.push(layer);
      };

      // earlier types e.g. vehicles and officers are so numerous
      // they are obscuring the more important icons such as incidents
      // this makes it so the order of features determines the z index
      Object.values(singularToPluralTypeMap).forEach((type) => {
        const polygonLayer = polygonable.includes(type);

        // the top layer for icons
        initSourceAndLayer(
          itemSources.current,
          itemLayers.current,
          type,
          1,
          ICON_ZINDEX,
          getIconsOnlyStyle,
          isShown(type),
          !polygonLayer // don't cluster if it's a polygon type
        );

        // the lower layer for stale icons or polygons (so icons are always on top)
        // stale vehicles/officers don't use polygons so they have a higher z-index
        // than polygons (but lower than non-stale) and use the icons only style.
        // types that have polygon areas have a lower z-index so they are beneath
        // non-stale and stale icons
        initSourceAndLayer(
          itemSourcesLower.current,
          itemLayersLower.current,
          type,
          1,
          polygonLayer ? POLY_ZINDEX : STALE_ZINDEX,
          polygonLayer ? getPolygonsOnlyStyle : getIconsOnlyStyle,
          // if not a polygon layer, it can have an additional pred if stale layers are hidden
          polygonLayer ? isShown(type) : isShown(type) && isShown(STALE),
          !polygonLayer // don't cluster if it's a polygon type
        );

        // the filtered out layer - has the lowest z-index of all, everything is above it
        initSourceAndLayer(
          itemSourcesFilteredOut.current,
          itemLayersFilteredOut.current,
          type,
          0.3,
          FILTERED_ZINDEX,
          getDefaultStyle,
          // filtered layers can have an additonal pred if filtered layers are hidden
          isShown(type) && isShown(FILTERED),
          true
        );
      });

      // these are the special layerToggles which apply to one or more layers
      layerToggles[STALE] = { show: isShown(STALE) };
      layerToggles[FILTERED] = { show: isShown(FILTERED) };

      setLayerToggles(layerToggles);

      hoverSource.current = new VectorSource();
      selectSource.current = new VectorSource();

      let lastView = undefined;

      try {
        const lastViewSetting = window.localStorage.getItem(LIVE_MAP_VIEW_KEY);
        const lastViewOptions =
          !!lastViewSetting && JSON.parse(lastViewSetting);

        if (lastViewOptions) {
          lastView = new View(lastViewOptions);
        }
      } catch (e) {
        lastView = undefined;
        console.warn('Could not load map view', e);
      }

      mapRef.current = new Map({
        target: mapDiv.current,
        layers: [
          ...itemLayersFilteredOut.current,
          ...itemLayersLower.current,
          ...itemLayers.current,
        ],
        interactions: defaultInteractions({
          pinchRotate: false,
          altShiftDragRotate: false,
        }),
        view:
          lastView ||
          new View({
            center: [0, 0],
            zoom: 2,
          }),
        controls: defaultControls({
          attribution: true,
          attributionOptions: { collabsible: false, collapsed: false },
          rotate: false,
          zoom: false,
        }),
      });

      // these are "unmanaged" layers and always appear on top
      const hoverLayer = new VectorLayer({
        source: hoverSource.current,
        style: getHoverStyle,
      });
      const selectLayer = new VectorLayer({
        source: selectSource.current,
        style: getSelectStyle,
      });
      hoverLayer.setMap(mapRef.current);
      selectLayer.setMap(mapRef.current);

      // debouncing hover so it's not called so often (any time pointer
      // moves) & slows map
      mapRef.current.on('pointermove', debouncedHover);

      function selectFeatureOnMap(feature) {
        const { type: individualType, route } = feature.getProperties();
        let type = singularToPluralTypeMap[individualType];
        let id = feature.getId();

        setSourceFeature(type, id, selectSource.current);

        return { type, id, route };
      }

      function handleClick(event) {
        hoverSource.current.clear();
        const feature = featureFromEvent(event);

        if (feature) {
          let item = selectFeatureOnMap(feature);

          // tell parent page it's selected
          // handleSelect(item);
          onSelectRef.current(item);
        } else {
          selectSource.current.clear();
          // handleSelect(null);
          onSelectRef.current(null);
        }

        // const oe = event.originalEvent;
        // const clonedEvent = new oe.constructor(oe.type, oe);
        // mapDiv.current.dispatchEvent(clonedEvent);
        mapDiv.current.focus();
      }

      mapRef.current.on('singleclick', handleClick);

      setBaseLayers(mapRef.current);

      // do we have more than one tile layer? if so show the satellite icon
      setAllowSatellite(
        mapRef.current
          .getLayers()
          .getArray()
          .filter(
            (layer) => layer instanceof TileLayer || layer instanceof ImageLayer
          ).length > 1
      );

      // when all the layers are ready (tile layers may need to load)
      // preload all the live icons
      function loadIconsWhenBaseLayersLoaded(e) {
        const someLoading = e.target
          .getLayerStatesArray()
          .some((s) => s.sourceState === 'loading');

        if (!someLoading) {
          preloadLiveIcons({
            authorisedTypes: authorisedTypes.map(
              (type) => pluralToSingularTypeMap[type]
            ),
            allowRotations,
          });
          e.target.un('change', loadIconsWhenBaseLayersLoaded);
        }
      }

      mapRef.current
        .getLayerGroup()
        .on('change', loadIconsWhenBaseLayersLoaded);

      if (!lastView) {
        // fit the map to the config's mapExtent as a default
        mapRef.current
          .getView()
          .fit(
            applyTransform(
              window.config.mapExtent,
              getTransform('EPSG:4326', 'EPSG:3857')
            )
          );
      }
    },
    [
      type,
      showLabels,
      debouncedHover,
      layerVisibilities,
      onSelect,
      setSourceFeature,
      authorisedTypes,
      allowRotations,
    ] // useEffect deps
  );

  const LIVE_MAP_VIEW_KEY = 'liveMapView';
  function saveMapView(view) {
    try {
      window.localStorage.setItem(
        LIVE_MAP_VIEW_KEY,
        JSON.stringify({
          center: view.getCenter(),
          zoom: view.getZoom(),
        })
      );
    } catch {}
  }

  const debouncedSaveMapView = _.debounce(saveMapView, 500);

  // any time the zoom disabled state changes, reregister the moveend callback
  // so we can check if we need to disable the buttons
  useEffect(() => {
    const handleMoveEnd = (e) => {
      const view = e.map.getView();
      const zoom = view.getZoom();
      const minZoom = view.getMinZoom();
      const maxZoom = view.getMaxZoom();

      const zoomInDisabled = zoom === maxZoom;
      const zoomOutDisabled = zoom === minZoom;
      if (
        zoomDisabled.in !== zoomInDisabled ||
        zoomDisabled.out !== zoomOutDisabled
      ) {
        setZoomDisabled({ in: zoomInDisabled, out: zoomOutDisabled });
      }

      // also (debounced) save the last position for reading later
      debouncedSaveMapView(view);
    };

    // any time it moves check if we need to disable zoom buttons
    mapRef.current.on('moveend', handleMoveEnd);

    return () => {
      mapRef.current.un('moveend', handleMoveEnd);
    };
  }, [zoomDisabled, debouncedSaveMapView]);

  // if an item is hovered add it to the hover source to highlight
  useEffect(() => {
    hoverSource.current.clear();
    const { type, id } = hoveredItem;
    if (id) {
      setSourceFeature(type, id, hoverSource.current);
    }
  }, [hoveredItem, setSourceFeature]);

  // if an item is first-selected, highlight it in the select source
  useEffect(() => {
    selectSource.current.clear();
    const { type, id } = selectedItem;
    if (id) {
      setSourceFeature(type, id, selectSource.current);
    }

    // refresh the map in case a polygon needs to be updated (selected colour)
    Object.keys(itemSourcesLower.current).forEach((type) =>
      itemSourcesLower.current[type].changeComplete()
    );
  }, [selectedItem, setSourceFeature]);

  // if the planFeatures changes add/remove the individual features to the map
  const previousPlanFeatures = usePrevious(planFeatures);
  useEffect(() => {
    function planFeatureToFeatureObj(planFeature) {
      const { geometry, properties } = planFeature;
      const { coordinates } = geometry;
      const { identifier: id, type, subtype, title, collectionId } = properties;
      const isMarker = type === 'Marker';
      const isPath = type === 'Path';

      let clusterPoint = null;
      if (isMarker) {
        clusterPoint = new Point(coordinates);
      } else if (isPath) {
        // get the mid point (~~ does a floor)
        clusterPoint = new Point(coordinates[~~(coordinates.length / 2)]);
      } else {
        clusterPoint = new Polygon(coordinates).getInteriorPoint();
      }

      clusterPoint &&
        clusterPoint.applyTransform(getTransform('EPSG:4326', 'EPSG:3857'));

      const zIndex = 100;

      return {
        geometry,
        id,
        type: 'Feature',
        properties: {
          clusterPoint,
          zIndex,
          type: 'plan',
          label: title,
          // planType: properties.type,
          subtype: subtype || type,
          isMarker,
          route: `/live/plans/${encodeURIComponent(
            collectionId
          )}/${encodeURIComponent(id)}`,
          filteredIn: true,
        },
      };
    }

    if (previousPlanFeatures !== planFeatures) {
      const iconSource = itemSources.current['plans'];
      const polySource = itemSourcesLower.current['plans'];

      // wipe the ones that were there
      if (previousPlanFeatures) {
        // otherwise we need to remove the previous ones
        previousPlanFeatures.forEach((planFeature) => {
          const {
            properties: { type, identifier },
          } = planFeature;
          const isMarker = type === 'Marker';
          const sourceToRemoveFrom = isMarker ? iconSource : polySource;

          const found = sourceToRemoveFrom.getFeatureById(identifier);
          found && sourceToRemoveFrom.removeFeature(found);
        });
      }

      // add the new plan features?
      if (planFeatures) {
        planFeatures.forEach((planFeature) => {
          const featureObj = planFeatureToFeatureObj(planFeature);
          const mapFeature = new GeoJSON().readFeature(featureObj, {
            featureProjection: 'EPSG:3857',
          });

          // add to icons if it's a marker otherwise put it in poly
          const sourceToAddTo = featureObj.properties.isMarker
            ? iconSource
            : polySource;
          sourceToAddTo.addFeature(mapFeature);
        });
      }

      iconSource.changeComplete();
      polySource.changeComplete();
    }
  }, [planFeatures, previousPlanFeatures]);

  const [waitingForMatch, setWaitingForMatch] = useState(false);
  const prevId = usePrevious(id);
  const prevSubId = usePrevious(subId);
  useEffect(() => {
    // if the id has changed try to select the feature in the select source
    if (id && (id !== prevId || subId !== prevSubId)) {
      // console.log("trying to find nav'd id - when id changes");
      let target = setSourceFeature(type, subId || id, selectSource.current);
      if (target) {
        // JL TODO if autozoom is added this gets uncommented
        // // zoom in to it (if mobile pad bottom so it centres on top)
        // mapRef.current
        //   .getView()
        //   .fit(target.getGeometry().getExtent(), fitOptionsMobile);
      } else {
        // it hasn't loaded yet, keep a lookout for it
        setWaitingForMatch(true);
      }
    } else if (waitingForMatch && !id) {
      // if we were waiting for a match and there's no longer an id stop waiting
      setWaitingForMatch(false);
    }
  }, [type, id, setSourceFeature, prevId, waitingForMatch, subId, prevSubId]);

  // if there's no item selected clear the selected layer
  useEffect(() => {
    if (prevId && !id) {
      selectSource.current.clear();
    }
  }, [prevId, id, selectSource]);

  // // when an item arrives and it was selected, // don't zoom to it
  // const matchArrived = waitingForMatch && getFeatureByTypeId(type, id);
  // useEffect(() => {
  //   if (matchArrived) {
  //     // console.log("waiting for match");
  //     let target = getFeatureByTypeId(type, id);
  //     if (target) {
  //       // // console.log("got match");
  //       selectSource.current.clear();
  //       selectSource.current.addFeature(target);

  //       // // zoom in to it (if mobile pad bottom so it centres on top)
  //       // mapRef.current
  //       //   .getView()
  //       //   .fit(target.getGeometry().getExtent(), fitOptionsMobile);

  //       log('Read', type, id);

  //       setWaitingForMatch(false);
  //     }
  //   }
  // }, [
  //   type,
  //   id,
  //   setWaitingForMatch,
  //   matchArrived,
  //   getFeatureByTypeId,
  //   fitOptionsMobile,
  //   setSourceFeature,
  //   features
  // ]);

  // if the filtered ids change, check for differences and move just the ones that need it
  useEffect(() => {
    const filtersChanged =
      (prevFilteredInIdsByType || filteredInIdsByType) !== filteredInIdsByType;

    if (featuresUpdated || filtersChanged) {
      Object.keys(pluralToSingularTypeMap).forEach((type) => {
        let filteredInSource = itemSources.current[type];
        let filteredInSourceLower = itemSourcesLower.current[type];
        let filteredOutSource = itemSourcesFilteredOut.current[type];
        let needsRedraw = false;

        // add or update any features properties and location
        if (featuresUpdated && updatedIds[type]) {
          needsRedraw = true;
          updatedIds[type].forEach((id) => {
            upsertMapFeature({
              features,
              type,
              id,
              itemSources,
              itemSourcesLower,
              itemSourcesFilteredOut,
              getFeatureByTypeId,
              filteredInIdsByType,
              followedIdsByType,
            });
          });

          // if we're waiting for an item to show up (e.g. nav'd to vehicle/V1) and the
          // item finally shows up, select it
          const targetId = subId || id;
          if (waitingForMatch && updatedIds[type].includes(targetId)) {
            const feature = toFeatureObj(
              features[type][targetId],
              type,
              filteredInIdsByType,
              followedIdsByType
            );

            if (feature) {
              const selectedFeature = new GeoJSON().readFeature(feature, {
                featureProjection: 'EPSG:3857',
              });
              selectSource.current.clear();
              selectSource.current.addFeature(selectedFeature);
              setWaitingForMatch(false);
            }
          }
        }

        // if the filters have changed we may need to move items to a different layer
        if (filtersChanged) {
          const all = features[type] || {};
          const prev = prevFilteredInIdsByType[type] || all;
          const curr = filteredInIdsByType[type] || all;

          // how many have changed?
          const nowFilteredIn = Object.keys(curr).filter((k) => !prev[k]);
          const nowFilteredOut = Object.keys(prev).filter((k) => !curr[k]);

          const totalChanges = nowFilteredIn.length + nowFilteredOut.length;
          if (totalChanges) {
            needsRedraw = true;
            // console.log(`${type} has ${nowFilteredIn.length} in and ${nowFilteredOut.length} out`);

            nowFilteredOut.forEach((id) => {
              const outcast =
                filteredInSource.getFeatureById(id) ||
                filteredInSourceLower.getFeatureById(id);

              if (outcast) {
                outcast.setProperties({
                  ...outcast.getProperties(),
                  filteredIn: false,
                });
                safeRemoveFeature(filteredInSource, id);
                safeRemoveFeature(filteredInSourceLower, id);

                filteredOutSource.addFeature(outcast);
              }
            });

            nowFilteredIn.forEach((id) => {
              const incast = filteredOutSource.getFeatureById(id);

              if (incast) {
                incast.setProperties({
                  ...incast.getProperties(),
                  filteredIn: true,
                });
                safeRemoveFeature(filteredOutSource, id);

                if (shouldGoInUpper(incast)) {
                  filteredInSource.addFeature(incast);
                }
                if (shouldGoInLower(incast)) {
                  filteredInSourceLower.addFeature(incast);
                }
              }
            });
          }
        }

        if (needsRedraw) {
          filteredInSource.changeComplete();
          filteredInSourceLower.changeComplete();
          filteredOutSource.changeComplete();
        }
      });
    }
  }, [
    featuresUpdated,
    getFeatureByTypeId,
    updatedIds,
    features,
    filteredInIdsByType,
    prevFilteredInIdsByType,
    id,
    subId,
    waitingForMatch,
    followedIdsByType,
  ]);

  // whenever type changes, make it the foremost layer
  useEffect(() => {
    function updateZIndex(layer, baseIndex) {
      const layerType = layer.get('title');
      layer.setZIndex(
        baseIndex +
          (layerType === type
            ? layerZIndexOrder.length
            : layerZIndexOrder.indexOf(layerType))
      );
    }

    itemLayers.current.forEach((layer) => updateZIndex(layer, ICON_ZINDEX));
    itemLayersFilteredOut.current.forEach((layer) =>
      updateZIndex(layer, FILTERED_ZINDEX)
    );
  }, [type]);

  // whenever layerVisibilities change, make layers visible/invisible
  const prevLayerVisibilities = usePrevious(layerVisibilities);
  useEffect(() => {
    function isShown(type) {
      // show layer if it's not in the state or use the state value
      return !(type in layerVisibilities) || layerVisibilities[type];
    }

    if (
      Object.keys(layerToggles).length > 0 &&
      prevLayerVisibilities !== layerVisibilities
    ) {
      const showStale = isShown('stale');
      const showFiltered = isShown('filtered');

      Object.keys(layerToggles).forEach((type) => {
        let shown = isShown(type);
        layerToggles[type].show = shown;

        if (layerToggles[type].layers) {
          //layerToggles[type].layers.forEach(layer => layer.setVisible(shown));
          layerToggles[type].layers[0].setVisible(shown);
          const showLower = shown && (polygonable.includes(type) || showStale);
          layerToggles[type].layers[1].setVisible(showLower);
          layerToggles[type].layers[2].setVisible(shown && showFiltered);
        }
      });

      mapRef.current &&
        mapRef.current.getLayers().forEach((layer) => layer.changed());

      setLayerToggles({ ...layerToggles });
    }
  }, [layerVisibilities, layerToggles, prevLayerVisibilities]);

  useEffect(() => {
    // follow every item in the followed list
    let extent = createEmpty();

    // if the list changed or at least one of the items moved, move the map to follow
    const someMoved = Object.keys(followedIdsByType).some((type) =>
      Object.keys(followedIdsByType[type]).some(
        (id) => type in updatedIds && updatedIds[type].includes(id)
      )
    );

    if (
      (prevFollowedIdsByType || followedIdsByType) !== followedIdsByType ||
      someMoved
    ) {
      let typesThatNeedUpdating = {};

      // unfollow any that aren't followed now
      Object.keys(prevFollowedIdsByType).forEach((type) => {
        Object.keys(prevFollowedIdsByType[type]).forEach((id) => {
          if (!followedIdsByType[type]?.[id]) {
            let feature = getFeatureByTypeId(type, id);

            if (feature) {
              typesThatNeedUpdating[type] = true;

              const props = feature.getProperties();
              feature.setProperties({
                ...props,
                followed: false,
                zIndex: statusToZIndex(props.status, type),
              });
            }
          }
        });
      });

      Object.keys(followedIdsByType).forEach((type) => {
        Object.keys(followedIdsByType[type]).forEach((id) => {
          let feature = getFeatureByTypeId(type, id);

          if (feature) {
            typesThatNeedUpdating[type] = true;

            // if the follow attribute isn't set (followed but hasn't moved), set it
            const props = feature.getProperties();
            if (!props.followed) {
              feature.setProperties({
                ...props,
                followed: true,
                zIndex: 10,
              });
            }

            let geom = feature.getGeometry();
            if (geom) {
              extend(extent, geom.getExtent());
            }
          }
        });
      });

      if (!isEmpty(extent)) {
        let view = mapRef.current.getView();
        let zoom = view.getZoom();
        view.fit(extent, { maxZoom: zoom, duration: 300 });
      }

      Object.keys(typesThatNeedUpdating).forEach((type) => {
        itemSources.current[type]?.changeComplete();
        itemSourcesLower.current[type]?.changeComplete();
        itemSourcesFilteredOut.current[type]?.changeComplete();
      }
      );
    }
  }, [
    followedIdsByType,
    updatedIds,
    getFeatureByTypeId,
    prevFollowedIdsByType,
  ]);

  function fitByIdForType(extent, dictionary, type) {
    Object.keys(dictionary[type]).forEach((id) => {
      const feature = getFeatureByTypeId(type, id);
      if (feature) {
        const geom = feature.getGeometry();
        if (geom) {
          extend(extent, geom.getExtent());
        }
      }
    });
  }

  function fitByIdForAllTypes(extent, dictionary) {
    Object.keys(dictionary).forEach((type) => {
      // normally we would only care about items in the top layer (itemSources)
      // but if an incident is selected the filters are overridden and luckily
      // contain all the ids of what we need to zoom in on
      fitByIdForType(extent, dictionary, type);
    });
  }

  // fit to everything that's filtered in...
  const fitMap = () => {
    let extent = createEmpty();
    let options = fitOptions;

    if (type === 'tags' || type === 'callSigns') {
      const groupType = type === 'tags' ? tags : callSigns;
      // fit to a tag's items
      // if we're just looking at a tag (has no features of its own) fit to its items
      if (id) {
        fitByIdForAllTypes(extent, groupType[id]?.itemsByType || {});
      } else {
        // this case doesn't really make sense but keeps it consistent with other types
        Object.keys(tags).forEach((id) =>
          fitByIdForAllTypes(extent, groupType[id]?.itemsByType || {})
        );
      }
    } else if (
      // fit to an individual (unless it's the owner of a filter overide)
      id &&
      (!filteredInIdsByTypeOverride ||
        filteredInIdsByTypeOverride?.owner !== id)
    ) {
      // if there's an individual id selected zoom into that
      // unless it's the owner of an override filter
      let target = setSourceFeature(type, id, selectSource.current);
      if (target && target.getGeometry()) {
        // zoom in to it (if mobile pad bottom so it centres on top)
        extent = target.getGeometry().getExtent();
        // if id selected need to zoom to top half because of drawer
        options = fitOptionsMobile;
      }
    } else if (filteredInIdsByTypeOverride) {
      // if it's there's a filter override fit to all items of all types
      // e.g. an incident override might have vehicles, people and incident types
      fitByIdForAllTypes(extent, filteredInIdsByTypeOverride);
    } else if (filteredInIdsByType[type]) {
      // otherwise if the type is filtered fit to everything of that type
      fitByIdForType(extent, filteredInIdsByType, type);
    } else {
      // zoom to everything filtered in on only the selected type
      // so if you have filtered to two vehicles and click fit map
      // it doesn't zoom out to all the locations that are filtered in
      //Object.keys(itemSources.current).forEach(type => {
      let topFeatures = itemSources.current[type].getFeatures();

      // add in the stale items IF they are shown
      if (isShown(layerVisibilities, STALE)) {
        topFeatures = topFeatures.concat(
          itemSourcesLower.current[type]?.getFeatures() || []
        );
      }

      topFeatures.forEach((feature) => {
        if (feature.getProperties().filteredIn) {
          let geom = feature.getGeometry();
          if (geom) {
            extend(extent, geom.getExtent());
          }
        }
      });
      // });
    }

    if (!isEmpty(extent)) {
      view.fit(extent, options);
    }
  };

  const zoom = (step) => {
    if (!view) {
      return;
    }

    const currentZoom = view.getZoom();
    const maxZoom = view.getMaxZoom();
    const minZoom = view.getMinZoom();
    const newZoom = currentZoom + step;

    // if the new one is ok, set the zoom
    if (minZoom <= newZoom && newZoom <= maxZoom) {
      view.setZoom(newZoom);
    }
  };

  function handleLayerMenuClick(event) {
    setLayerAnchorEl(event.currentTarget);
  }

  function handleLayerMenuClose() {
    setLayerAnchorEl(null);
  }

  function handleLegendOpen(event) {
    setLegendAnchorEl(event.currentTarget);
  }

  function handleLegendClose() {
    setLegendAnchorEl(null);
  }

  const SatelliteOrMapIcon = satelliteMapVisible ? MapIcon : SatelliteIcon;
  let staleFilterDivider = false;

  // console.log('Map render');
  return (
    <div className={classes.container}>
      <div
        id="map"
        className={classes.map}
        // onMouseOut={debouncedHover.cancel}
        ref={mapDiv}
      />
      <IconButton
        title="Zoom In"
        className={classes.zoomInButton}
        aria-label="Zoom In"
        color={satelliteMapVisible ? 'inherit' : 'default'}
        disabled={zoomDisabled.in}
        onClick={() => zoom(+1)}
      >
        <ZoomInIcon />
      </IconButton>
      <IconButton
        title="Zoom Out"
        className={classes.zoomOutButton}
        aria-label="Zoom Out"
        color={satelliteMapVisible ? 'inherit' : 'default'}
        disabled={zoomDisabled.out}
        onClick={() => zoom(-1)}
      >
        <ZoomOutIcon />
      </IconButton>
      <IconButton
        title="Fit"
        className={classes.fitButton}
        aria-label="Fit"
        color={satelliteMapVisible ? 'inherit' : 'default'}
        onClick={fitMap}
      >
        <ZoomOutMapIcon />
      </IconButton>
      <IconButton
        title="Labels"
        className={classes.labelButton}
        aria-label="Toggle labels"
        color={satelliteMapVisible ? 'inherit' : 'default'}
        onClick={() => changeSettings({ showLabels: !showLabels.current })}
      >
        {showLabels.current ? <LabelIcon /> : <LabelOffIcon />}
      </IconButton>
      <div
        className={classes.layerToggleArea}
        // style={{ opacity: satelliteMapVisible ? 1 : 0.53 }}
      >
        <Menu
          open={Boolean(layerAnchorEl)}
          anchorEl={layerAnchorEl}
          onClose={handleLayerMenuClose}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
        >
          {Object.keys(layerToggles)
            .filter((type) =>
              authorisedTypes
                .filter((t) => !['tags', 'callSigns'].includes(t))
                .concat(STALE, FILTERED)
                .includes(type)
            )
            .map((type) => {
              const Icon = typeIcons[type];
              let showDivider = false;
              if (!staleFilterDivider && ['stale', 'filtered'].includes(type)) {
                staleFilterDivider = true;
                showDivider = true;
              }

              return (
                <div key={type}>
                  {showDivider && <Divider />}
                  <MenuItem
                    onClick={() => {
                      dispatch({
                        type: UPDATE_LIVE_LAYER_VISIBILITIES,
                        payload: {
                          ...layerVisibilities,
                          [type]: !(type in layerVisibilities
                            ? layerVisibilities[type]
                            : true),
                        },
                      });
                    }}
                  >
                    <ListItemIcon className={classes.iconStyle}>
                      <Icon />
                    </ListItemIcon>
                    <Typography
                      variant="subtitle1"
                      className={classes.layerMenuItemText}
                    >
                      {layerToggles[type].show ? 'Hide' : 'Show'} {type}
                    </Typography>

                    {/* <React.Fragment>
                      <div className={classes.diagonalContainer}>
                          <Icon />
                          {layerToggles[type].show || (
                            <React.Fragment>
                              <div className={classes.diagonalWhite}>|</div>
                              <div className={classes.diagonal}>|</div>
                            </React.Fragment>
                          )}
                      </div>
                    </React.Fragment> 

                    <div className={classes.layerMenuItemText}>
                      {layerToggles[type].show ? 'Hide' : 'Show'} {type}
                    </div>*/}
                  </MenuItem>
                </div>
              );
            })}
          {allowSatellite && (
            <MenuItem
              key="satelliteOrMap"
              onClick={() => {
                if (mapRef.current) {
                  mapRef.current
                    .getLayers()
                    .item(satelliteMapVisible ? 0 : 1)
                    .setVisible(true);
                  mapRef.current
                    .getLayers()
                    .item(satelliteMapVisible ? 1 : 0)
                    .setVisible(false);
                  setSatelliteMapVisible(!satelliteMapVisible);
                }
              }}
            >
              <SatelliteOrMapIcon
                title={satelliteMapVisible ? 'Road Map' : 'Satellite Map'}
              />
              <div className={classes.layerMenuItemText}>
                {satelliteMapVisible ? 'Road Map' : 'Satellite Map'}
              </div>
            </MenuItem>
          )}
        </Menu>
        <IconButton
          color={satelliteMapVisible ? 'inherit' : 'default'}
          className={classes.layerToggleIcon}
          onClick={handleLayerMenuClick}
          title="Layers"
        >
          <LayersIcon />
        </IconButton>
        {/* {layerSelectionOpen && (
          <div className={classes.layerToggleList}>
            {Object.keys(layerToggles)
              .filter(type => authorisedTypes.includes(type))
              .map((type, index) => {
                const Icon = typeIcons[type];
                return (
                  <IconButton
                    key={type}
                    title={type}
                    color={satelliteMapVisible ? 'inherit' : 'default'}
                    style={index !== 0 ? { marginTop: -12 } : undefined}
                    className={classes.layerToggleButton}
                    onClick={() => {
                      dispatch({
                        type: UPDATE_LIVE_LAYER_VISIBILITIES,
                        payload: {
                          ...layerVisibilities,
                          [type]: !(type in layerVisibilities
                            ? layerVisibilities[type]
                            : true)
                        }
                      });
                    }}
                  >
                    <React.Fragment>
                      <Icon />
                      {layerToggles[type].show || (
                        <div className={classes.diagonal}>|</div>
                      )}
                    </React.Fragment>
                  </IconButton>
                );
              })}
            {allowSatellite && (
              <IconButton
                title={satelliteMapVisible ? 'Road Map' : 'Satellite Map'}
                className={classes.layerToggleButton}
                aria-label="Map"
                color={satelliteMapVisible ? 'inherit' : 'default'}
                onClick={() => {
                  if (mapRef.current) {
                    mapRef.current
                      .getLayers()
                      .item(satelliteMapVisible ? 0 : 1)
                      .setVisible(true);
                    mapRef.current
                      .getLayers()
                      .item(satelliteMapVisible ? 1 : 0)
                      .setVisible(false);
                    setSatelliteMapVisible(!satelliteMapVisible);
                    // this.setState({
                    //   satelliteMapVisible: !this.state.satelliteMapVisible
                    // });
                  }
                }}
              >
                {satelliteMapVisible ? <MapIcon /> : <SatelliteIcon />}
              </IconButton>
            )}
          </div>
        )}
        <IconButton
          color={satelliteMapVisible ? 'inherit' : 'default'}
          className={classes.layerToggleIcon}
          onClick={() => setLayerSelectionOpen(o => !o)}
        >
          <LayersIcon />
        </IconButton> */}
      </div>

      <div className={classes.legendToggleArea}>
        <Popover
          open={Boolean(legendAnchorEl)}
          anchorEl={legendAnchorEl}
          onClose={handleLegendClose}
          width="100%"
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          PaperProps={{
            className: classes.legendPopover,
          }}
        >
          <LiveLegend authorisedTypes={authorisedTypes} />
        </Popover>
        <IconButton
          // color={satelliteMapVisible ? 'inherit' : 'default'}
          className={classes.legendToggleButton}
          onClick={handleLegendOpen}
          title="Legend"
        >
          <LegendIcon />
        </IconButton>
      </div>
    </div>
  );
}

export default LiveMap;
